import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import { CreditCard, Building2, MapPin, Mail, Phone, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { countries } from '../utils/countries';
import { validateVKN, validateTCKN } from '../utils/taxValidation';

// ─── Ülke alan kodları ────────────────────────────────────────────────────────
const countryDialCodes = [
  { code: 'TR', dial: '+90', flag: '🇹🇷', name: 'Turkey' },
  { code: 'US', dial: '+1',  flag: '🇺🇸', name: 'United States' },
  { code: 'GB', dial: '+44', flag: '🇬🇧', name: 'United Kingdom' },
  { code: 'DE', dial: '+49', flag: '🇩🇪', name: 'Germany' },
  { code: 'FR', dial: '+33', flag: '🇫🇷', name: 'France' },
  { code: 'IT', dial: '+39', flag: '🇮🇹', name: 'Italy' },
  { code: 'ES', dial: '+34', flag: '🇪🇸', name: 'Spain' },
  { code: 'NL', dial: '+31', flag: '🇳🇱', name: 'Netherlands' },
  { code: 'BE', dial: '+32', flag: '🇧🇪', name: 'Belgium' },
  { code: 'AT', dial: '+43', flag: '🇦🇹', name: 'Austria' },
  { code: 'CH', dial: '+41', flag: '🇨🇭', name: 'Switzerland' },
  { code: 'SE', dial: '+46', flag: '🇸🇪', name: 'Sweden' },
  { code: 'NO', dial: '+47', flag: '🇳🇴', name: 'Norway' },
  { code: 'DK', dial: '+45', flag: '🇩🇰', name: 'Denmark' },
  { code: 'FI', dial: '+358', flag: '🇫🇮', name: 'Finland' },
  { code: 'PL', dial: '+48', flag: '🇵🇱', name: 'Poland' },
  { code: 'PT', dial: '+351', flag: '🇵🇹', name: 'Portugal' },
  { code: 'GR', dial: '+30', flag: '🇬🇷', name: 'Greece' },
  { code: 'RU', dial: '+7',  flag: '🇷🇺', name: 'Russia' },
  { code: 'UA', dial: '+380', flag: '🇺🇦', name: 'Ukraine' },
  { code: 'AZ', dial: '+994', flag: '🇦🇿', name: 'Azerbaijan' },
  { code: 'GE', dial: '+995', flag: '🇬🇪', name: 'Georgia' },
  { code: 'SA', dial: '+966', flag: '🇸🇦', name: 'Saudi Arabia' },
  { code: 'AE', dial: '+971', flag: '🇦🇪', name: 'UAE' },
  { code: 'IL', dial: '+972', flag: '🇮🇱', name: 'Israel' },
  { code: 'IN', dial: '+91', flag: '🇮🇳', name: 'India' },
  { code: 'CN', dial: '+86', flag: '🇨🇳', name: 'China' },
  { code: 'JP', dial: '+81', flag: '🇯🇵', name: 'Japan' },
  { code: 'KR', dial: '+82', flag: '🇰🇷', name: 'South Korea' },
  { code: 'AU', dial: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: 'NZ', dial: '+64', flag: '🇳🇿', name: 'New Zealand' },
  { code: 'BR', dial: '+55', flag: '🇧🇷', name: 'Brazil' },
  { code: 'MX', dial: '+52', flag: '🇲🇽', name: 'Mexico' },
  { code: 'CA', dial: '+1',  flag: '🇨🇦', name: 'Canada' },
  { code: 'AR', dial: '+54', flag: '🇦🇷', name: 'Argentina' },
  { code: 'EG', dial: '+20', flag: '🇪🇬', name: 'Egypt' },
  { code: 'ZA', dial: '+27', flag: '🇿🇦', name: 'South Africa' },
  { code: 'NG', dial: '+234', flag: '🇳🇬', name: 'Nigeria' },
  { code: 'PK', dial: '+92', flag: '🇵🇰', name: 'Pakistan' },
  { code: 'BD', dial: '+880', flag: '🇧🇩', name: 'Bangladesh' },
  { code: 'ID', dial: '+62', flag: '🇮🇩', name: 'Indonesia' },
  { code: 'MY', dial: '+60', flag: '🇲🇾', name: 'Malaysia' },
  { code: 'TH', dial: '+66', flag: '🇹🇭', name: 'Thailand' },
  { code: 'VN', dial: '+84', flag: '🇻🇳', name: 'Vietnam' },
  { code: 'PH', dial: '+63', flag: '🇵🇭', name: 'Philippines' },
  { code: 'SG', dial: '+65', flag: '🇸🇬', name: 'Singapore' },
  { code: 'IE', dial: '+353', flag: '🇮🇪', name: 'Ireland' },
  { code: 'CZ', dial: '+420', flag: '🇨🇿', name: 'Czech Republic' },
  { code: 'RO', dial: '+40', flag: '🇷🇴', name: 'Romania' },
  { code: 'HU', dial: '+36', flag: '🇭🇺', name: 'Hungary' },
  { code: 'BG', dial: '+359', flag: '🇧🇬', name: 'Bulgaria' },
  { code: 'HR', dial: '+385', flag: '🇭🇷', name: 'Croatia' },
];

// ─── Anlık hata mesajları (onChange) ─────────────────────────────────────────
function getInlineError(field, value, formData, lang) {
  const tr = lang === 'tr';
  switch (field) {
    case 'tc_kimlik_no':
      if (!value) return null;
      if (!/^\d+$/.test(value)) return tr ? 'Sadece rakam girin' : 'Digits only';
      if (value.length < 11)    return tr ? `${11 - value.length} hane eksik` : `${11 - value.length} more digits needed`;
      if (value.length === 11 && !validateTCKN(value)) return tr ? 'Geçersiz TC Kimlik No' : 'Invalid Turkish ID number';
      return null;

    case 'tax_number':
      if (!value) return null;
      if (formData.country === 'TR') {
        if (!/^\d+$/.test(value)) return tr ? 'Sadece rakam girin' : 'Digits only';
        if (value.length < 10)   return tr ? `${10 - value.length} hane eksik` : `${10 - value.length} more digits needed`;
        if (value.length === 10 && !validateVKN(value)) return tr ? 'Geçersiz Vergi Kimlik Numarası' : 'Invalid Tax Number (VKN checksum failed)';
      }
      return null;

    default:
      return null;
  }
}

// ─── Bileşen ──────────────────────────────────────────────────────────────────
function Billing({ user, onLogout }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';

  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [message,  setMessage]  = useState('');
  const [messageType, setMessageType] = useState('');

  const [formData, setFormData] = useState({
    first_name:           '',
    last_name:            '',
    email:                user.email || '',
    phone_number:         '',
    phone_country_code:   'TR',
    country:              'TR',
    address_line:         '',
    city:                 '',
    district:             '',   // ← YENİ: İlçe / State / Province
    postal_code:          '',
    is_tax_liable:        false,
    tc_kimlik_no:         '',
    company_name:         '',
    tax_number:           '',
    tax_office:           '',
    vat_number:           '',
    declaration_accepted: false,
  });

  const [inlineErrors, setInlineErrors] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => { fetchBillingProfile(); }, []);

  // ─── Yardımcılar ────────────────────────────────────────────────
  const isTR          = formData.country === 'TR';
  const needsTCKN     = isTR  && !formData.is_tax_liable;
  const needsTRComp   = isTR  &&  formData.is_tax_liable;
  const needsGlobalComp = !isTR && formData.is_tax_liable;

  const parsePhoneNumber = (fullPhone) => {
    if (!fullPhone) return { code: 'TR', number: '' };
    const sorted = [...countryDialCodes].sort((a, b) => b.dial.length - a.dial.length);
    for (const c of sorted) {
      if (fullPhone.startsWith(c.dial)) return { code: c.code, number: fullPhone.slice(c.dial.length) };
    }
    return { code: 'TR', number: fullPhone };
  };

  // ─── Fetch ──────────────────────────────────────────────────────
  const fetchBillingProfile = async () => {
    try {
      const response = await axios.get('/billing/profile');
      if (response.data.profile) {
        const profile = response.data.profile;
        const parsed  = parsePhoneNumber(profile.phone_number);
        setFormData(prev => ({
          ...prev,
          ...profile,
          district:           profile.district || '',
          phone_number:       parsed.number,
          phone_country_code: parsed.code,
        }));
      }
    } catch (error) {
      console.error('Error fetching billing profile:', error);
    } finally {
      setLoading(false);
    }
  };

  // ─── Input handler ───────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({ ...prev, [name]: newValue }));

    const err = getInlineError(name, type === 'checkbox' ? checked : value, { ...formData, [name]: value }, lang);
    setInlineErrors(prev => ({ ...prev, [name]: err }));
    setValidationErrors(prev => prev.filter(f => f !== name));
  };

  // ─── Ülke değişimi ───────────────────────────────────────────────
  const handleCountryChange = (e) => {
    const newCountry = e.target.value;
    setFormData(prev => ({
      ...prev,
      country:              newCountry,
      phone_country_code:   countryDialCodes.find(c => c.code === newCountry) ? newCountry : prev.phone_country_code,
      is_tax_liable:        false,
      tc_kimlik_no:         '',
      company_name:         '',
      tax_number:           '',
      tax_office:           '',
      vat_number:           '',
      district:             '',
      declaration_accepted: false,
    }));
    setInlineErrors({});
  };

  const handlePhoneCountryChange = (e) => {
    setFormData(prev => ({ ...prev, phone_country_code: e.target.value }));
  };

  // ─── Vergi mükellefiyim toggle ───────────────────────────────────
  const handleTaxLiableToggle = () => {
    setFormData(prev => ({
      ...prev,
      is_tax_liable:        !prev.is_tax_liable,
      tc_kimlik_no:         '',
      company_name:         '',
      tax_number:           '',
      tax_office:           '',
      vat_number:           '',
      declaration_accepted: false,
    }));
    setInlineErrors({});
  };

  // ─── Validasyon ──────────────────────────────────────────────────
  const validateForm = () => {
    const errors = [];

    if (!formData.first_name   || formData.first_name.trim().length   < 2) errors.push('first_name');
    if (!formData.last_name    || formData.last_name.trim().length    < 2) errors.push('last_name');
    if (!formData.email        || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.push('email');
    if (!formData.phone_number || formData.phone_number.replace(/\D/g, '').length < 7) errors.push('phone_number');
    if (!formData.country)       errors.push('country');
    if (!formData.address_line || formData.address_line.trim().length < 5) errors.push('address_line');
    if (!formData.city         || formData.city.trim().length         < 2) errors.push('city');

    // TR için ilçe zorunlu
    if (isTR && (!formData.district || formData.district.trim().length < 2)) errors.push('district');

    if (needsTCKN) {
      if (!validateTCKN(formData.tc_kimlik_no)) errors.push('tc_kimlik_no');
    }

    if (needsTRComp) {
      if (!formData.company_name || formData.company_name.trim().length < 2) errors.push('company_name');
      if (!validateVKN(formData.tax_number)) errors.push('tax_number');
      if (!formData.tax_office   || formData.tax_office.trim().length   < 2) errors.push('tax_office');
    }

    if (needsGlobalComp) {
      if (!formData.company_name || formData.company_name.trim().length < 2) errors.push('company_name');
    }

    if (!formData.declaration_accepted) errors.push('declaration_accepted');

    setValidationErrors(errors);
    return errors.length === 0;
  };

  // ─── Submit ──────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setMessageType('');

    if (!validateForm()) {
      setMessage(lang === 'tr'
        ? 'Lütfen tüm zorunlu alanları doğru şekilde doldurun'
        : 'Please fill all required fields correctly');
      setMessageType('error');
      return;
    }

    setSaving(true);
    try {
      const dialCode   = countryDialCodes.find(c => c.code === formData.phone_country_code)?.dial || '+90';
      const cleanPhone = formData.phone_number.replace(/^0+/, '');
      const { phone_country_code, ...restData } = formData;

      const submitData = {
        ...restData,
        phone_number:    `${dialCode}${cleanPhone}`,
        tax_number:  needsTRComp     ? formData.tax_number : '',
        vat_number:  needsGlobalComp ? formData.tax_number : '',
        declared_at: formData.declaration_accepted ? new Date().toISOString() : null,
        customer_type: formData.is_tax_liable ? 'company' : 'individual',
      };

      await axios.post('/billing/profile', submitData);

      setMessage(lang === 'tr'
        ? 'Fatura bilgileriniz başarıyla kaydedildi'
        : 'Billing information saved successfully');
      setMessageType('success');
      fetchBillingProfile();
    } catch (error) {
      const validationErrs = error.response?.data?.validation_errors;
      if (validationErrs?.length > 0) {
        setValidationErrors(validationErrs);
        setMessage(lang === 'tr' ? 'Bazı alanlar hatalı veya eksik' : 'Some fields are invalid or missing');
      } else {
        setMessage(lang === 'tr' ? 'Kaydetme başarısız oldu' : 'Failed to save billing information');
      }
      setMessageType('error');
    } finally {
      setSaving(false);
    }
  };

  // ─── Hata yardımcıları ───────────────────────────────────────────
  const hasError = (fieldName) => validationErrors.includes(fieldName);

  const fieldClass = (name) => {
    if (hasError(name) || inlineErrors[name]) return 'error';
    return '';
  };

  const FieldHint = ({ name }) => {
    const err = inlineErrors[name];
    if (!err) return null;
    return (
      <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px' }}>
        <AlertCircle size={12} /> {err}
      </p>
    );
  };

  // ─── Loading ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="dashboard-layout">
        <Sidebar user={user} onLogout={onLogout} />
        <div className="dashboard-main">
          <div className="loading">{lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}</div>
        </div>
      </div>
    );
  }

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <div className="dashboard-layout">
      <Sidebar user={user} onLogout={onLogout} />
      <main className="dashboard-main">

        <header className="dashboard-header">
          <div>
            <h1>
              <CreditCard size={32} style={{ verticalAlign: 'middle', marginRight: '12px' }} />
              {lang === 'tr' ? 'Fatura Bilgilerim' : 'Billing Information'}
            </h1>
            <p>{lang === 'tr'
              ? 'Fatura için gerekli bilgilerinizi buradan yönetin'
              : 'Manage your billing information for invoices'}</p>
          </div>
        </header>

        {/* Alert */}
        {message && (
          <div className={`alert ${messageType === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '24px' }}>
            {messageType === 'success'
              ? <CheckCircle size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              : <AlertCircle  size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />}
            {message}
          </div>
        )}

        {/* Bilgi Kutusu */}
        <div style={{ background: '#f0f9ff', border: '1px solid #0ea5e9', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
          <h3 style={{ marginTop: 0, color: '#0369a1', fontSize: '16px' }}>
            ℹ️ {lang === 'tr' ? 'Önemli Bilgi' : 'Important Information'}
          </h3>
          <ul style={{ marginBottom: 0, color: '#0c4a6e', padding: '18px' }}>
            <li>{lang === 'tr' ? 'Bu bilgiler faturalarınızda kullanılacaktır.' : 'This information will appear on your invoices.'}</li>
            <li>{lang === 'tr' ? 'Ödeme yapmadan önce formu eksiksiz doldurmanız gerekmektedir.' : 'You must complete this form before making a payment.'}</li>
            <li>{lang === 'tr' ? 'Telefon numaranız ödeme işlemleri için PayTR\'ye gönderilecektir.' : 'Your phone number will be sent to PayTR for payment processing.'}</li>
            <li>{lang === 'tr' ? 'TC Kimlik No ve VKN kontrolleri anlık olarak doğrulanmaktadır.' : 'TCKN and VKN numbers are validated in real-time.'}</li>
          </ul>
        </div>

        <form onSubmit={handleSubmit}>

          {/* ══════════════════════════════════════════════════════════
              TEMEL BİLGİLER
          ══════════════════════════════════════════════════════════ */}
          <div className="section">
            <h3>{lang === 'tr' ? 'Temel Bilgiler' : 'Basic Information'}</h3>
            <div className="form-grid">

              <div className="form-group">
                <label htmlFor="first_name">{lang === 'tr' ? 'Ad' : 'First Name'} <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="text" id="first_name" name="first_name" value={formData.first_name}
                  onChange={handleInputChange} className={hasError('first_name') ? 'error' : ''} required />
              </div>

              <div className="form-group">
                <label htmlFor="last_name">{lang === 'tr' ? 'Soyad' : 'Last Name'} <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="text" id="last_name" name="last_name" value={formData.last_name}
                  onChange={handleInputChange} className={hasError('last_name') ? 'error' : ''} required />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <Mail size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  Email <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input type="email" id="email" name="email" value={formData.email}
                  onChange={handleInputChange} className={hasError('email') ? 'error' : ''} required />
              </div>

              <div className="form-group">
                <label htmlFor="phone_number">
                  <Phone size={14} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                  {lang === 'tr' ? 'Telefon Numarası' : 'Phone Number'} <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <div style={{ display: 'flex' }}>
                  <select value={formData.phone_country_code} onChange={handlePhoneCountryChange}
                    style={{
                      minWidth: '130px', maxWidth: '150px', borderRadius: '8px 0 0 8px', borderRight: 'none',
                      padding: '10px 8px', fontSize: '14px', background: '#f8fafc', cursor: 'pointer',
                      appearance: 'none', WebkitAppearance: 'none',
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center', paddingRight: '24px',
                    }}>
                    {countryDialCodes.map(c => (
                      <option key={c.code} value={c.code}>{c.flag} {c.dial}</option>
                    ))}
                  </select>
                  <input type="tel" id="phone_number" name="phone_number" value={formData.phone_number}
                    onChange={handleInputChange} className={hasError('phone_number') ? 'error' : ''}
                    placeholder={formData.phone_country_code === 'TR' ? '5XX XXX XX XX' : lang === 'tr' ? 'Telefon numaranız' : 'Your phone number'}
                    required style={{ borderRadius: '0 8px 8px 0', flex: 1 }} />
                </div>
                <p className="form-help" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                  {lang === 'tr' ? 'Ödeme işlemleri için kullanılacaktır' : 'Will be used for payment transactions'}
                </p>
              </div>

            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════
              ADRES
          ══════════════════════════════════════════════════════════ */}
          <div className="section">
            <h3>
              <MapPin size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              {lang === 'tr' ? 'Adres Bilgileri' : 'Address Information'}
            </h3>

            <div className="form-group">
              <label htmlFor="country">{lang === 'tr' ? 'Ülke' : 'Country'} <span style={{ color: '#ef4444' }}>*</span></label>
              <select id="country" name="country" value={formData.country}
                onChange={handleCountryChange} className={hasError('country') ? 'error' : ''} required>
                {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="address_line">{lang === 'tr' ? 'Adres' : 'Address'} <span style={{ color: '#ef4444' }}>*</span></label>
              <textarea id="address_line" name="address_line" value={formData.address_line}
                onChange={handleInputChange} rows={3} className={hasError('address_line') ? 'error' : ''}
                placeholder={lang === 'tr' ? 'Mahalle, Sokak, Bina No, Daire No' : 'Street, Building No, Apartment No'} required />
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="city">{lang === 'tr' ? 'Şehir' : 'City'} <span style={{ color: '#ef4444' }}>*</span></label>
                <input type="text" id="city" name="city" value={formData.city}
                  onChange={handleInputChange} className={hasError('city') ? 'error' : ''} required />
              </div>

              <div className="form-group">
                <label htmlFor="district">
                  {isTR
                    ? (lang === 'tr' ? 'İlçe' : 'District')
                    : (lang === 'tr' ? 'İlçe / Eyalet / Bölge' : 'District / State / Province')}
                  {isTR && <span style={{ color: '#ef4444' }}> *</span>}
                </label>
                <input
                  type="text"
                  id="district"
                  name="district"
                  value={formData.district || ''}
                  onChange={handleInputChange}
                  className={hasError('district') ? 'error' : ''}
                  placeholder={isTR
                    ? (lang === 'tr' ? 'Örn: Yüreğir, Seyhan' : 'e.g. Yüreğir')
                    : (lang === 'tr' ? 'Örn: California, Bavaria' : 'e.g. California, Bavaria')}
                />
                {hasError('district') && (
                  <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                    {lang === 'tr' ? 'İlçe bilgisi zorunludur' : 'District is required'}
                  </p>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="postal_code">
                  {lang === 'tr' ? 'Posta Kodu' : 'Postal Code'} ({lang === 'tr' ? 'önerilir' : 'recommended'})
                </label>
                <input type="text" id="postal_code" name="postal_code" value={formData.postal_code} onChange={handleInputChange} />
              </div>
            </div>
          </div>

          {/* ══════════════════════════════════════════════════════════
              VERGİ BİLGİLERİ
          ══════════════════════════════════════════════════════════ */}
          <div className="section">
            <h3>
              <Building2 size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
              {lang === 'tr' ? 'Vergi Bilgileri' : 'Tax Information'}
            </h3>

            <div
              onClick={handleTaxLiableToggle}
              style={{
                display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '16px',
                background: formData.is_tax_liable ? '#f0fdf4' : '#f8fafc',
                border: `1px solid ${formData.is_tax_liable ? '#86efac' : '#e2e8f0'}`,
                borderRadius: '8px', cursor: 'pointer', marginBottom: '20px', transition: 'all 0.2s',
              }}
            >
              <input type="checkbox" id="is_tax_liable" name="is_tax_liable"
                checked={formData.is_tax_liable} onChange={() => {}}
                style={{ marginTop: '2px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }} />
              <div>
                <label htmlFor="is_tax_liable" style={{ fontWeight: 600, cursor: 'pointer', display: 'block', marginBottom: '4px' }}>
                  {lang === 'tr' ? 'Vergi mükellefiyim' : 'I am a tax liable entity'}
                </label>
                <span style={{ fontSize: '13px', color: '#64748b' }}>
                  {lang === 'tr'
                    ? 'Şirket, şahıs şirketi veya serbest meslek sahibi olarak fatura almak istiyorum'
                    : 'I want to receive invoices as a company, sole proprietor, or self-employed entity'}
                </span>
              </div>
            </div>

            {/* TR bireysel → TCKN */}
            {needsTCKN && (
              <div className="form-group">
                <label htmlFor="tc_kimlik_no">
                  TC Kimlik No <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text" id="tc_kimlik_no" name="tc_kimlik_no"
                  value={formData.tc_kimlik_no} onChange={handleInputChange}
                  maxLength={11} inputMode="numeric"
                  className={fieldClass('tc_kimlik_no')}
                  placeholder="11 haneli TC Kimlik No" required
                />
                <FieldHint name="tc_kimlik_no" />
                {hasError('tc_kimlik_no') && !inlineErrors.tc_kimlik_no && (
                  <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                    {lang === 'tr' ? 'Geçerli bir TC Kimlik No girin' : 'Enter a valid Turkish ID number'}
                  </p>
                )}
              </div>
            )}

            {/* TR mükellef → VKN + Vergi Dairesi */}
            {needsTRComp && (
              <>
                <div className="form-group">
                  <label htmlFor="company_name">
                    {lang === 'tr' ? 'Şirket / Ticaret Unvanı' : 'Company Name'} <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input type="text" id="company_name" name="company_name"
                    value={formData.company_name} onChange={handleInputChange}
                    className={hasError('company_name') ? 'error' : ''} required />
                </div>
                <div className="form-grid">
                  <div className="form-group">
                    <label htmlFor="tax_number">
                      {lang === 'tr' ? 'Vergi Kimlik Numarası (VKN)' : 'Tax Number (VKN)'} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input type="text" id="tax_number" name="tax_number"
                      value={formData.tax_number} onChange={handleInputChange}
                      maxLength={10} inputMode="numeric"
                      className={fieldClass('tax_number')}
                      placeholder={lang === 'tr' ? '10 haneli VKN' : '10-digit VKN'} required />
                    <FieldHint name="tax_number" />
                    {hasError('tax_number') && !inlineErrors.tax_number && (
                      <p style={{ color: '#ef4444', fontSize: '12px', marginTop: '4px' }}>
                        {lang === 'tr' ? 'Geçerli bir VKN girin' : 'Enter a valid VKN'}
                      </p>
                    )}
                  </div>
                  <div className="form-group">
                    <label htmlFor="tax_office">
                      {lang === 'tr' ? 'Vergi Dairesi' : 'Tax Office'} <span style={{ color: '#ef4444' }}>*</span>
                    </label>
                    <input type="text" id="tax_office" name="tax_office"
                      value={formData.tax_office} onChange={handleInputChange}
                      className={hasError('tax_office') ? 'error' : ''}
                      placeholder={lang === 'tr' ? 'Örn: Kadıköy' : 'e.g. Kadıköy'} required />
                  </div>
                </div>
              </>
            )}

            {/* Global mükellef */}
            {needsGlobalComp && (
              <>
                <div className="form-group">
                  <label htmlFor="company_name">
                    {lang === 'tr' ? 'Şirket Adı' : 'Company Name'} <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input type="text" id="company_name" name="company_name"
                    value={formData.company_name} onChange={handleInputChange}
                    className={hasError('company_name') ? 'error' : ''} required />
                </div>
                <div className="form-group">
                  <label htmlFor="tax_number">
                    {lang === 'tr' ? 'Vergi / KDV Numarası' : 'VAT / Tax ID'} ({lang === 'tr' ? 'önerilir' : 'recommended'})
                  </label>
                  <input type="text" id="tax_number" name="tax_number"
                    value={formData.tax_number} onChange={handleInputChange}
                    placeholder="EU VAT, EIN, GST, ABN, etc." />
                  <p className="form-help" style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                    {lang === 'tr'
                      ? 'Faturanızda görünmesini istediğiniz vergi numarası (isteğe bağlı)'
                      : 'Tax or VAT number to appear on your invoice (optional)'}
                  </p>
                </div>
              </>
            )}
          </div>

          {/* ══════════════════════════════════════════════════════════
              BEYAN ONAYI
          ══════════════════════════════════════════════════════════ */}
          <div className="section" style={{
            background: hasError('declaration_accepted') ? '#fef2f2' : '#fafafa',
            border: `1px solid ${hasError('declaration_accepted') ? '#fca5a5' : '#e2e8f0'}`,
            borderRadius: '8px',
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
              <input type="checkbox" id="declaration_accepted" name="declaration_accepted"
                checked={formData.declaration_accepted} onChange={handleInputChange}
                style={{ marginTop: '3px', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0 }} required />
              <label htmlFor="declaration_accepted" style={{ cursor: 'pointer', fontSize: '14px', lineHeight: '1.6', color: '#374151' }}>
                {lang === 'tr'
                  ? 'Yukarıda beyan ettiğim vergi ve kimlik bilgilerinin doğru ve eksiksiz olduğunu; yanlış beyan durumunda tüm hukuki ve mali sorumluluğun tarafıma ait olduğunu kabul ediyorum.'
                  : 'I confirm that the tax and identity information I have provided is accurate and complete. I accept full legal and financial responsibility for any false or misleading declaration.'}
                {' '}<span style={{ color: '#ef4444' }}>*</span>
              </label>
            </div>
            {hasError('declaration_accepted') && (
              <p style={{ color: '#ef4444', fontSize: '13px', marginTop: '8px', marginLeft: '30px' }}>
                {lang === 'tr' ? 'Bu beyanı kabul etmeniz zorunludur.' : 'You must accept this declaration to continue.'}
              </p>
            )}
          </div>

          {/* ══════════════════════════════════════════════════════════
              KAYDET
          ══════════════════════════════════════════════════════════ */}
          <div className="section centered_area" style={{ borderTop: 'none' }}>
            <button type="submit" className="btn-primary" disabled={saving} style={{ minWidth: '200px' }}>
              <Save size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
              {saving
                ? (lang === 'tr' ? 'Kaydediliyor...' : 'Saving...')
                : (lang === 'tr' ? 'Kaydet' : 'Save')}
            </button>
          </div>

        </form>
      </main>
    </div>
  );
}

export default Billing;
